/* Organique Kuwait (r10) – product videos: the product gallery and the home "See it in use" row.
   Mohammad's rules: native Shopify video; poster first (the image under the video shows at once, the <video>
   has preload="none" and no source); no video bytes before the window load event; plays muted, inline and
   looped only while at least half of it is on screen and, in a slideshow, only on the current slide; pauses
   when scrolled or swiped away; a speaker button turns the sound on; never autoplay with sound.
   Shoppers who ask for reduced motion (or data saving) get a play button instead of autoplay.
   r13 (speed, 1 Oct 2026): (1) Shopify's HLS stream offers 1080p, 720p and 480p, and the browser's own adaptive
   streaming climbed to 1080p (606 x 1080) even for a 255 px card on a phone; now the smallest rendition that is
   sharp enough for the size the video is shown at is played (same rule as the MP4s: 1.5 x its CSS width at most),
   e.g. 720p for the home cards and the gallery Reels on phones. On a slow connection the browser still adapts.
   (2) A video only starts once it has stayed in view for a moment, and one that leaves before it has started
   playing stops downloading, so scrolling or swiping past it downloads nothing. */
const doc = document;
const motionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
const saveData = !!(navigator.connection && navigator.connection.saveData);
const manualStart = () => saveData || !!(motionQuery && motionQuery.matches);
let pageLoaded = doc.readyState === 'complete';
const players = new Set();
const refreshAll = () => players.forEach((player) => player.update());
/* r13: how long a video must stay in view before it starts (scrolling or swiping past downloads nothing) */
const START_DELAY = 350;
/* r13: on a slow connection (as far as the browser tells) its own adaptive streaming stays in charge */
const slowNetwork = () => {
  const connection = /** @type {any} */ (navigator).connection;
  if (!connection) return false;
  return /^(slow-2g|2g|3g)$/.test(connection.effectiveType || '') || (connection.downlink > 0 && connection.downlink < 2);
};

/* r13: the smallest HLS rendition that is sharp enough. Reads the master playlist (once per video) and returns the
   URL of one rendition's playlist, or '' to keep the master playlist (when it can't be read, lists other groups
   such as separate audio, or the size on screen isn't known yet). */
const masterPlaylists = new Map();
function pickRendition(master, shownWidth) {
  let base;
  try {
    base = new URL(master, window.location.href);
  } catch (error) {
    return Promise.resolve('');
  }
  let body = masterPlaylists.get(base.href);
  if (!body) {
    body = fetch(base.href)
      .then((response) => (response.ok ? response.text() : ''))
      .catch(() => '');
    masterPlaylists.set(base.href, body);
  }
  return body.then((text) => {
    if (!/^#EXTM3U/.test(text) || /#EXT-X-(MEDIA|KEY|SESSION-KEY):/.test(text)) return '';
    const lines = text.split(/\r?\n/);
    const list = [];
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].indexOf('#EXT-X-STREAM-INF:') !== 0) continue;
      const size = /RESOLUTION=(\d+)x(\d+)/.exec(lines[i]);
      let uri = '';
      for (let j = i + 1; j < lines.length; j++) {
        const line = lines[j].trim();
        if (!line) continue;
        if (line.charAt(0) !== '#') uri = line;
        break;
      }
      if (!size || !uri) return '';
      list.push({ width: Number(size[1]), height: Number(size[2]), url: new URL(uri, base.href).href });
    }
    if (list.length < 2) return '';
    list.sort((a, b) => a.width - b.width);
    const top = list[list.length - 1];
    const shown = shownWidth(top.width, top.height);
    if (!shown) return '';
    const need = shown * Math.min(window.devicePixelRatio || 1, 1.5);
    return (list.find((item) => item.width >= need) || top).url;
  });
}

if (!pageLoaded) {
  window.addEventListener(
    'load',
    () => {
      pageLoaded = true;
      refreshAll();
    },
    { once: true }
  );
}
doc.addEventListener('visibilitychange', refreshAll);
window.addEventListener('pageshow', refreshAll);
if (motionQuery && motionQuery.addEventListener) motionQuery.addEventListener('change', refreshAll);

/* "In view" = at least half of the video on screen (or, for a video taller than the screen, most of the screen) */
const viewObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const player = /** @type {any} */ (entry.target);
            const box = entry.boundingClientRect;
            const seen = entry.intersectionRect;
            const tallerThanScreen = box.height > window.innerHeight * 0.9;
            player.inView =
              entry.isIntersecting &&
              (entry.intersectionRatio >= 0.5 ||
                (tallerThanScreen && seen.height >= window.innerHeight * 0.6 && seen.width >= box.width * 0.5));
            player.update();
          }
        },
        { threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1] }
      )
    : null;

/* Slide changes (aria-hidden on the gallery's slides) and dialogs opening or closing */
const attributeObserver = new MutationObserver(refreshAll);
const watched = new WeakSet();
function watch(element, attributes) {
  if (!element || watched.has(element)) return;
  watched.add(element);
  attributeObserver.observe(element, { attributes: true, attributeFilter: attributes });
}
function openModal() {
  try {
    return doc.querySelector('dialog:modal');
  } catch (error) {
    return doc.querySelector('dialog[open]');
  }
}

/* The speaker button. Captured on window so the theme's own click handling on the slide (the zoom dialog) and
   on cards does not also run. */
window.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    const button = target instanceof Element ? target.closest('[data-org-video-btn]') : null;
    if (!button) return;
    const player = /** @type {any} */ (button.closest('org-video'));
    if (!player || typeof player.toggle !== 'function') return;
    event.preventDefault();
    event.stopPropagation();
    player.toggle();
  },
  true
);

class OrgVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.button = this.querySelector('[data-org-video-btn]');
    if (!this.video) return;
    if (!this.ready) {
      this.ready = true;
      this.inView = !viewObserver;
      this.sound = false;
      this.started = false;
      this.blocked = false;
      this.hasAudio = null;
      this.timer = 0;
      this.pick = '';
      this.preparing = null;
      this.video.addEventListener('playing', () => {
        this.setAttribute('data-ready', '');
        this.detectAudio();
      });
    }
    this.slide = this.closest('slideshow-slide');
    watch(this.slide, ['aria-hidden']);
    doc.querySelectorAll('dialog').forEach((dialog) => watch(dialog, ['open']));
    players.add(this);
    if (viewObserver) viewObserver.observe(this);
    this.render();
    this.update();
  }

  disconnectedCallback() {
    players.delete(this);
    if (viewObserver) viewObserver.unobserve(this);
    this.cancel();
    this.stop(true);
  }

  isCurrentSlide() {
    return !this.slide || this.slide.getAttribute('aria-hidden') !== 'true';
  }

  coveredByDialog() {
    const modal = openModal();
    return !!modal && !modal.contains(this);
  }

  /* true while a tap is needed first: reduced motion, data saving, or the browser refused muted autoplay */
  tapFirst() {
    return manualStart() || this.blocked;
  }

  shouldPlay() {
    if (!this.video || !this.inView || doc.hidden || !this.isCurrentSlide() || this.coveredByDialog()) return false;
    if (this.tapFirst()) return this.started;
    return pageLoaded || this.started;
  }

  update() {
    if (!this.video) return;
    if (this.shouldPlay()) {
      /* already loading or playing, or the shopper tapped play: go on at once */
      if (this.video.getAttribute('src') || this.started) return this.play();
      /* r13: the first start waits until the video has stayed in view for a moment */
      if (!this.timer) this.timer = window.setTimeout(() => this.begin(), START_DELAY);
    } else {
      this.cancel();
      this.stop(false);
    }
  }

  begin() {
    this.timer = 0;
    if (!this.shouldPlay()) return;
    this.prepare();
    const go = () => {
      if (!this.timer && this.shouldPlay()) this.play();
    };
    if (this.preparing) this.preparing.then(go);
    else go();
  }

  cancel() {
    if (this.timer) window.clearTimeout(this.timer);
    this.timer = 0;
  }

  /* r13: pick the HLS rendition for this video once, when it is about to start (see pickRendition) */
  prepare() {
    if (this.preparing || this.pick) return;
    const video = /** @type {HTMLVideoElement} */ (this.video);
    const hls = this.dataset.hls || '';
    if (!hls || !video.canPlayType('application/vnd.apple.mpegurl') || slowNetwork()) return;
    this.preparing = pickRendition(hls, (width, height) => this.shownWidth(width, height)).then((url) => {
      this.pick = url;
    });
  }

  /* r13: the CSS width the picture takes on screen: cards fill their box (cover), the gallery shows it whole (contain) */
  shownWidth(width, height) {
    const boxWidth = this.clientWidth;
    const boxHeight = this.clientHeight;
    if (!boxWidth || !boxHeight || !width || !height || !this.video) return 0;
    const cover = window.getComputedStyle(this.video).objectFit === 'cover';
    const scale = cover
      ? Math.max(boxWidth / width, boxHeight / height)
      : Math.min(boxWidth / width, boxHeight / height);
    return width * scale;
  }

  /* HLS where the browser plays it natively (Safari, iOS, Chrome), in the rendition picked by prepare() when there is
     one; otherwise the smallest MP4 that is sharp enough */
  source() {
    const video = /** @type {HTMLVideoElement} */ (this.video);
    const hls = this.dataset.hls || '';
    if (hls && video.canPlayType('application/vnd.apple.mpegurl')) return this.pick || hls;
    const parts = (this.dataset.mp4 || '').split(/\s+/).filter(Boolean);
    const list = [];
    for (let i = 0; i + 1 < parts.length; i += 2) list.push({ size: Number(parts[i]) || 0, url: parts[i + 1] });
    list.sort((a, b) => a.size - b.size);
    if (!list.length) return hls;
    const box = Math.min(this.clientWidth || 0, this.clientHeight || this.clientWidth || 0);
    const need = box * Math.min(window.devicePixelRatio || 1, 1.5);
    const pick = list.find((item) => item.size >= need) || list[list.length - 1];
    return pick.url;
  }

  play() {
    const video = /** @type {HTMLVideoElement} */ (this.video);
    if (!video.getAttribute('src')) {
      const src = this.source();
      if (!src) return;
      video.src = src;
    }
    video.muted = !this.sound;
    if (video.paused) {
      const promise = video.play();
      if (promise && promise.catch) {
        promise.catch((error) => {
          if (error && error.name === 'AbortError') return;
          if (!video.muted) {
            /* the browser refused playback with sound: fall back to muted */
            this.sound = false;
            video.muted = true;
            this.render();
            video.play().catch(() => {});
          } else if (!this.started) {
            /* muted autoplay refused (e.g. iPhone low-power mode): show the play button instead */
            this.blocked = true;
            this.removeAttribute('data-playing');
            this.render();
            /* r13: and stop loading it until the shopper taps play */
            this.update();
          }
        });
      }
    }
    this.setAttribute('data-playing', '');
  }

  stop(release) {
    const video = /** @type {HTMLVideoElement} */ (this.video);
    if (!video) return;
    /* never comes back with sound on its own */
    if (this.sound) {
      this.sound = false;
      this.render();
    }
    video.muted = true;
    if (!video.paused) video.pause();
    this.removeAttribute('data-playing');
    if (this.tapFirst() && this.started) {
      this.started = false;
      this.render();
    }
    /* r13: a video that leaves the screen before it has started playing stops downloading (it starts again next time) */
    if (!release && !this.started && !this.hasAttribute('data-ready') && video.getAttribute('src')) release = true;
    if (release && video.getAttribute('src')) {
      video.removeAttribute('src');
      video.load();
    }
  }

  toggle() {
    if (this.tapFirst() && !this.started) {
      /* the play button: the shopper chose to play it, with sound */
      this.started = true;
      this.sound = true;
    } else {
      this.started = true;
      this.sound = !this.sound;
    }
    if (this.sound) {
      players.forEach((player) => {
        if (player !== this && player.sound) {
          player.sound = false;
          if (player.video) player.video.muted = true;
          player.render();
        }
      });
    }
    if (this.video) this.video.muted = !this.sound;
    this.render();
    this.update();
  }

  /* Show the speaker button only for videos that have sound */
  detectAudio() {
    if (this.hasAudio !== null || this.detecting) return;
    const video = /** @type {any} */ (this.video);
    const done = (value) => {
      this.detecting = false;
      this.hasAudio = value;
      this.render();
    };
    if (typeof video.mozHasAudio === 'boolean') return done(video.mozHasAudio);
    if (video.audioTracks && typeof video.audioTracks.length === 'number') return done(video.audioTracks.length > 0);
    if (typeof video.webkitAudioDecodedByteCount === 'number') {
      this.detecting = true;
      let tries = 0;
      const check = () => {
        if (!this.isConnected) return (this.detecting = false);
        if (video.webkitAudioDecodedByteCount > 0) return done(true);
        if (video.paused) return (this.detecting = false); /* try again on the next 'playing' */
        if (++tries > 8) return done(false);
        setTimeout(check, 250);
      };
      return check();
    }
    return done(true);
  }

  render() {
    const button = /** @type {HTMLElement | null} */ (this.button);
    if (!button) return;
    let state = this.sound ? 'on' : 'muted';
    if (this.tapFirst() && !this.started) state = 'play';
    button.hidden = !(state === 'play' || this.sound || this.hasAudio === true);
    button.dataset.state = state;
    const label = state === 'play' ? button.dataset.lPlay : state === 'on' ? button.dataset.lOff : button.dataset.lOn;
    if (label) button.setAttribute('aria-label', label);
  }
}

if (!customElements.get('org-video')) customElements.define('org-video', OrgVideo);
